import { logAmplitudeEventAllowCustomers } from 'amplitude/amplitude'
import { getAvailableActions } from 'ducks/myEnergy'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useNotify, useTranslate } from 'react-admin'
import { useDispatch } from 'react-redux'
import restClient from 'restClient'

const restClientInstance = restClient(window.API_ROOT + '/api')

const CLOOVER_SYNC_MAX_POLLS = 6
const CLOOVER_SYNC_INTERVAL_MS = 10000
const CLOOVER_KICKOFF_DEBOUNCE_MS = 300

type ClooverCheckoutState =
  | { status: 'idle' }
  | { status: 'pending' }
  | { status: 'ready'; checkoutUrl: string; isNewProject: boolean; financeProjectId?: string }
  | { status: 'failed' }

type ClooverCheckoutResult =
  | { status: 'ready'; checkoutUrl: string; isNewProject: boolean; financeProjectId?: string }
  | { status: 'failed' }

type PollResponse = {
  status?: string
  checkout_link?: string
  project_id?: string
  is_new_project?: boolean
  error?: string
}

async function pollForCheckoutLink(
  checkoutPath: string,
  transactionId: number,
  translate: (key: string) => string,
  shouldCancel?: () => boolean
): Promise<{ checkout_link: string; is_new_project: boolean; project_id?: string }> {
  const pollIntervalMs = 2500
  const pollMaxMs = 90000
  const deadline = Date.now() + pollMaxMs

  while (Date.now() < deadline) {
    if (shouldCancel?.()) {
      throw new Error('Cloover checkout cancelled.')
    }

    const poll = await restClientInstance('CUSTOM_GET', 'custom', {
      url: `${checkoutPath}?transaction_id=${transactionId}`,
    })
    const pollResponse = poll.data as PollResponse

    if (pollResponse.status === 'ready' && pollResponse.checkout_link) {
      return {
        checkout_link: pollResponse.checkout_link,
        is_new_project: pollResponse.is_new_project !== undefined ? pollResponse.is_new_project : true,
        project_id: pollResponse.project_id,
      }
    }

    if (pollResponse.status === 'failed') {
      throw new Error(pollResponse.error || translate('Failed to get Cloover checkout link.'))
    }

    await new Promise((r) => setTimeout(r, pollIntervalMs))
  }

  throw new Error(
    translate('Cloover application is taking longer than expected. Please close this dialog and try again.')
  )
}

function logIntegratedFinanceProjectCreated(
  integration: string | null | undefined,
  projectId: number,
  financeProjectId: string
) {
  logAmplitudeEventAllowCustomers('integrated_finance_project_created', {
    integration,
    project_id: projectId,
    finance_project_id: financeProjectId,
  })
}

interface Params {
  integration?: string | null
  orgId: number
  projectId: number
  systemUuid: string
  paymentOptionId: number
  /** When false, eager checkout kickoff is skipped (dialog not visible). */
  open: boolean
}

/**
 * Cloover-only state and side effects for the integrated finance disclaimer + iframe flow.
 * Generic dialog UI stays in DisclaimerRedirectDialog.
 */
export function useClooverApplication(params: Params) {
  const { integration, orgId, projectId, systemUuid, paymentOptionId, open } = params
  const translate = useTranslate()
  const notify = useNotify()
  const dispatch = useDispatch()
  const translateRef = useRef(translate)
  translateRef.current = translate
  const [checkoutUrl, setCheckoutUrl] = useState<string | null>(null)
  const [checkoutState, setCheckoutState] = useState<ClooverCheckoutState>({ status: 'idle' })
  const checkoutStateRef = useRef<ClooverCheckoutState>({ status: 'idle' })
  const inFlightRef = useRef<Promise<ClooverCheckoutResult> | null>(null)
  const kickoffGenerationRef = useRef<number>(0)
  const pollGenerationRef = useRef<number>(0)

  checkoutStateRef.current = checkoutState

  const runClooverSyncPollSequence = useCallback(async () => {
    if (integration !== 'cloover') return

    const syncPath = `orgs/${orgId}/projects/${projectId}/cloover/sync_status/`
    pollGenerationRef.current += 1
    const generation = pollGenerationRef.current
    let baselineStatus: string | null | undefined = undefined

    for (let attempt = 0; attempt < CLOOVER_SYNC_MAX_POLLS; attempt++) {
      if (pollGenerationRef.current !== generation) break

      try {
        const res = await restClientInstance('CUSTOM_GET', 'custom', { url: syncPath })
        const data = res.data as { decision_status?: string | null }
        const currentStatus = data.decision_status ?? null

        if (baselineStatus === undefined) {
          baselineStatus = currentStatus
        } else if (currentStatus !== baselineStatus) {
          dispatch(getAvailableActions(orgId, projectId))
          break
        }
      } catch {
        // A single failed request is not fatal; keep polling.
      }

      if (attempt < CLOOVER_SYNC_MAX_POLLS - 1) {
        await new Promise<void>((resolve) => setTimeout(resolve, CLOOVER_SYNC_INTERVAL_MS))
      }
    }
  }, [dispatch, integration, orgId, projectId])

  const maybePollClooverSyncOnClose = useCallback(() => {
    if (integration === 'cloover' && checkoutUrl !== null) {
      void runClooverSyncPollSequence()
    }
  }, [checkoutUrl, integration, runClooverSyncPollSequence])

  useEffect(() => {
    if (integration !== 'cloover' || !open) {
      return
    }

    const gen = ++kickoffGenerationRef.current
    const checkoutPath = `orgs/${orgId}/projects/${projectId}/systems/${systemUuid}/${paymentOptionId}/cloover/checkout_link/`
    const isKickoffActive = () => kickoffGenerationRef.current === gen

    const kickoffPromise = (async (): Promise<ClooverCheckoutResult> => {
      await new Promise<void>((resolve) => setTimeout(resolve, CLOOVER_KICKOFF_DEBOUNCE_MS))

      if (!isKickoffActive()) {
        return { status: 'failed' }
      }

      try {
        const response = await restClientInstance('CUSTOM_POST', 'custom', {
          url: checkoutPath,
        })
        const data = response.data as {
          checkout_link?: string
          status?: string
          transaction_id?: number
          poll_for_update?: boolean
          is_new_project?: boolean
          project_id?: string
        }

        if (!isKickoffActive()) {
          return { status: 'failed' }
        }

        if (data.checkout_link) {
          const isNewProject = data.is_new_project !== undefined ? data.is_new_project : false
          const financeProjectId = data.project_id
          const ready: ClooverCheckoutResult = {
            status: 'ready',
            checkoutUrl: data.checkout_link,
            isNewProject,
            financeProjectId,
          }

          setCheckoutState({
            status: 'ready',
            checkoutUrl: data.checkout_link,
            isNewProject,
            financeProjectId,
          })

          if (isNewProject && financeProjectId !== undefined) {
            logIntegratedFinanceProjectCreated(integration, projectId, financeProjectId)
          }

          return ready
        }

        if (data.poll_for_update && data.transaction_id != null) {
          setCheckoutState({ status: 'pending' })

          try {
            const polled = await pollForCheckoutLink(
              checkoutPath,
              data.transaction_id,
              (key) => translateRef.current(key),
              () => !isKickoffActive()
            )

            if (!isKickoffActive()) {
              return { status: 'failed' }
            }

            const isNewProject = polled.is_new_project
            const financeProjectId = polled.project_id
            const ready: ClooverCheckoutResult = {
              status: 'ready',
              checkoutUrl: polled.checkout_link,
              isNewProject,
              financeProjectId,
            }

            setCheckoutState({
              status: 'ready',
              checkoutUrl: polled.checkout_link,
              isNewProject,
              financeProjectId,
            })

            if (isNewProject && financeProjectId !== undefined) {
              logIntegratedFinanceProjectCreated(integration, projectId, financeProjectId)
            }

            return ready
          } catch {
            if (!isKickoffActive()) {
              return { status: 'failed' }
            }

            setCheckoutState({ status: 'failed' })

            return { status: 'failed' }
          }
        }

        setCheckoutState({ status: 'failed' })
        return { status: 'failed' }
      } catch {
        if (!isKickoffActive()) {
          return { status: 'failed' }
        }
        setCheckoutState({ status: 'failed' })
        return { status: 'failed' }
      }
    })()

    inFlightRef.current = kickoffPromise

    return () => {
      kickoffGenerationRef.current += 1
    }
    // translate omitted from deps on purpose: use translateRef so a new useTranslate identity each
    // render does not restart kickoff (would duplicate POSTs and break inFlightRef handoff).
  }, [integration, open, orgId, paymentOptionId, projectId, systemUuid])

  const startClooverCheckout = useCallback(async (): Promise<boolean> => {
    if (integration !== 'cloover') {
      return false
    }

    const checkoutPath = `orgs/${orgId}/projects/${projectId}/systems/${systemUuid}/${paymentOptionId}/cloover/checkout_link/`

    const setIframeCheckoutUrl = (url: string) => {
      setCheckoutUrl(url)
    }

    const snapshot = checkoutStateRef.current

    if (snapshot.status === 'ready') {
      setIframeCheckoutUrl(snapshot.checkoutUrl)
      return true
    }

    // Await the eager kickoff when pending, or when state has not flushed to "pending" yet
    // but inFlightRef already holds the kickoff promise (fast Continue click).
    if (
      inFlightRef.current &&
      (snapshot.status === 'pending' || snapshot.status === 'idle' || snapshot.status === 'failed')
    ) {
      const result = await inFlightRef.current

      if (result.status === 'ready') {
        setIframeCheckoutUrl(result.checkoutUrl)

        return true
      }
    }

    try {
      const response = await restClientInstance('CUSTOM_POST', 'custom', {
        url: checkoutPath,
      })
      const data = response.data as {
        checkout_link?: string
        status?: string
        transaction_id?: number
        poll_for_update?: boolean
        is_new_project?: boolean
        project_id?: string
      }

      let checkoutLink = data.checkout_link
      let isNewProject = data.is_new_project !== undefined ? data.is_new_project : false
      let financeProjectId = data.project_id

      if (!checkoutLink && data.poll_for_update && data.transaction_id != null) {
        const polled = await pollForCheckoutLink(checkoutPath, data.transaction_id, translate)
        checkoutLink = polled.checkout_link
        isNewProject = polled.is_new_project
        financeProjectId = polled.project_id
      }

      if (!checkoutLink) {
        notify(translate('Failed to load Cloover application.'), 'error')
        return false
      }

      setCheckoutState({
        status: 'ready',
        checkoutUrl: checkoutLink,
        isNewProject,
        financeProjectId,
      })
      setIframeCheckoutUrl(checkoutLink)

      if (isNewProject && financeProjectId !== undefined) {
        logIntegratedFinanceProjectCreated(integration, projectId, financeProjectId)
      }

      return true
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : String(error)
      notify(message, 'error')
      return false
    }
  }, [integration, notify, orgId, paymentOptionId, projectId, systemUuid, translate])

  return {
    checkoutUrl,
    startClooverCheckout,
    maybePollClooverSyncOnClose,
  }
}
